// lib/crud-factory.ts
import { useToast } from '@/hooks/use-toast';
import { http as api } from '@/lib/http';
import type { ApiError } from '@/types/api/api-error';
import type { DeleteResponse } from '@/types/api/delete-response';
import type { FindAllParams } from '@/types/api/find-all-params';
import type { FindAllResponse } from '@/types/api/find-all-response';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function createCrudHooks<TEntity, TCreate, TUpdate = Partial<TCreate>>(
  model: string,
) {
  const keys = {
    all: [model] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (params?: FindAllParams<TEntity>) =>
      [...keys.lists(), JSON.stringify(params ?? {})] as const,
    detail: (id: string) => [...keys.all, 'detail', id] as const,
  };

  const apiLayer = {
    getAll: async (params?: FindAllParams<TEntity>) => {
      const res = await api.get<FindAllResponse<TEntity>>(model, {
        params: params ?? {},
      });

      return res.data;
    },

    getById: async (id: string) => {
      try {
        const res = await api.get<TEntity>(`${model}/${id}`);
        return res.data;
      } catch (e) {
        return null as TEntity;
      }
    },

    create: async (data: TCreate) => {
      const res = await api.post<{ success: boolean; data: TEntity }>(model, data);
      return res.data.data;
    },

    update: async (id: string, data: TUpdate) => {
      const res = await api.patch<{ success: boolean; data: TEntity }>(
        `${model}/${id}`,
        data,
      );
      return res.data.data;
    },

    delete: async (id: string) => {
      const res = await api.delete<DeleteResponse>(`${model}/${id}`);
      return res.data;
    },
  };

  function useList(params?: FindAllParams<TEntity>) {
    return useQuery<FindAllResponse<TEntity>, ApiError>({
      queryKey: keys.list(params),
      queryFn: () => apiLayer.getAll(params),
    });
  }

  function useDetail(id: string) {
    return useQuery<TEntity, ApiError>({
      queryKey: keys.detail(id),
      queryFn: () => apiLayer.getById(id),
      enabled: !!id,
    });
  }

  function useCreate() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<TEntity, ApiError, TCreate>({
      mutationFn: apiLayer.create,
      onSuccess: () => {
        queryClient.invalidateQueries();
        toast.success('Cadastro criado.');
      },
      onError: (e) => toast.error(e.message),
    });
  }

  function useUpdate() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<TEntity, ApiError, { id: string; data: TUpdate }>({
      mutationFn: ({ id, data }) => apiLayer.update(id, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries();
        toast.success('Alterações salvas.');
      },
      onError: (e) => toast.error(e.message),
    });
  }

  function useDelete() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<DeleteResponse, ApiError, string>({
      mutationFn: apiLayer.delete,
      onSuccess: () => {
        queryClient.invalidateQueries();
        toast.success('Registro excluído.');
      },
      onError: (e) => toast.error(e.message),
    });
  }

  return {
    keys,
    useList,
    useDetail,
    useCreate,
    useUpdate,
    useDelete,
  };
}
